import { Campaign } from '@models/Campaign'
import { campaignSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createCampaign as createCampaignAPI,
  deleteCampaign as deleteCampaignAPI,
  fetchCampaignById,
  fetchCampaigns,
  updateCampaign as updateCampaignAPI,
} from '@services/CampaignService'
import {
  baseReducers,
  getUUIDOrModelUUID,
  SBAPI,
  SBSelectRaw,
  SBStateUUID,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from './store'

const initialState: SBStateUUID<Campaign> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'campaign',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getCampaignsSuccess,
  setQuery: setCampaignQuery,
  reset: resetCampaignState,
  resetQueryAndIds: resetCampaignQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: string]: Campaign }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, string[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, string | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectCampaigns = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedCampaign = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectCampaignById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectCampaignsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedCampaign =
  (campaign: Campaign | string) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getUUIDOrModelUUID<Campaign>(campaign))
    )

export const getCampaigns = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchCampaigns(params),
    [campaignSchema],
    slice.actions
  )

export const createCampaign = (campaign: Campaign) =>
  SBAPI.CreateDispatched(
    createCampaignAPI(campaign),
    campaignSchema,
    slice.actions
  )

export const getCampaignWithId = (id: number) =>
  SBAPI.FetchDispatched(fetchCampaignById(id), campaignSchema, slice.actions)

export const updateCampaign = (campaign: Campaign) =>
  SBAPI.UpdateDispatched<Campaign>(
    updateCampaignAPI(campaign),
    campaign,
    slice.actions
  )

export const deleteCampaign = (campaign: Campaign) =>
  SBAPI.DestroyDispatched<Campaign>(
    deleteCampaignAPI(campaign),
    campaign,
    slice.actions
  )
